/* ================================================================
   WOLKE Theme JS — Vanilla ES2020, no dependencies
   ================================================================ */

'use strict';

/* ── Helpers ── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const fmt = cents => '€' + (cents / 100).toFixed(2).replace('.', ',');

/* ════════════════════════════════════
   SCROLL LOCK (shared by mobile menu + cart drawer)
   body { overflow:hidden } alone doesn't reliably stop scrolling in
   Safari. Pinning the body with position:fixed at its current scroll
   offset blocks it completely; also drops the header out of
   position:sticky (via body.scroll-locked in CSS) so it can never
   render above the fixed overlay regardless of WebKit's sticky/fixed
   stacking quirks.
════════════════════════════════════ */
let __scrollLockY = 0;
let __scrollLockCount = 0;
function lockScroll() {
  if (__scrollLockCount === 0) {
    __scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${__scrollLockY}px`;
    document.body.classList.add('scroll-locked');
  }
  __scrollLockCount++;
}
function unlockScroll() {
  __scrollLockCount = Math.max(0, __scrollLockCount - 1);
  if (__scrollLockCount === 0) {
    document.body.classList.remove('scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, __scrollLockY);
  }
}

/* ════════════════════════════════════
   MOBILE MENU
════════════════════════════════════ */
function openMobileMenu() {
  const m = document.getElementById('mobile-menu');
  m?.classList.add('is-open');
  lockScroll();
}
function closeMobileMenu() {
  const m = document.getElementById('mobile-menu');
  m?.classList.remove('is-open');
  unlockScroll();
}
window.openMobileMenu = openMobileMenu;
window.closeMobileMenu = closeMobileMenu;

/* ════════════════════════════════════
   CART DRAWER
════════════════════════════════════ */
async function openCartDrawer() {
  const d = document.getElementById('cart-drawer');
  d?.classList.add('is-open');
  lockScroll();
  await refreshCartDrawer();
}
function closeCartDrawer() {
  const d = document.getElementById('cart-drawer');
  d?.classList.remove('is-open');
  unlockScroll();
}
window.openCartDrawer = openCartDrawer;
window.closeCartDrawer = closeCartDrawer;

async function refreshCartDrawer() {
  const body = document.getElementById('cart-drawer-body');
  const subtotalEl = document.getElementById('cart-drawer-subtotal');
  if (!body) return;
  try {
    const cart = await fetch('/cart.js').then(r => r.json());
    updateCartCount(cart.item_count);
    if (cart.item_count === 0) {
      body.innerHTML = '<p class="cart-empty">Dein Warenkorb ist noch leer.</p>';
      if (subtotalEl) subtotalEl.textContent = '€0,00';
      return;
    }
    body.innerHTML = cart.items.map(item => `
      <div class="cart-item">
        ${item.featured_image?.url
          ? `<img class="cart-item__img" src="${item.featured_image.url}" alt="${item.title}" loading="lazy">`
          : `<div class="cart-item__img"></div>`}
        <div class="cart-item__info">
          <div class="cart-item__name">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${fmt(item.final_line_price)}</div>
          <div class="cart-item__qty" style="display:inline-flex;align-items:center;gap:12px;margin:6px 0;">
            <button onclick="changeCartItem('${item.key}', ${item.quantity - 1})" aria-label="Menge verringern" style="width:26px;height:26px;border:1px solid #ccc;border-radius:6px;background:#fff;font-size:15px;line-height:1;cursor:pointer;">&minus;</button>
            <span style="min-width:16px;text-align:center;font-size:14px;">${item.quantity}</span>
            <button onclick="changeCartItem('${item.key}', ${item.quantity + 1})" aria-label="Menge erhöhen" style="width:26px;height:26px;border:1px solid #ccc;border-radius:6px;background:#fff;font-size:15px;line-height:1;cursor:pointer;">+</button>
          </div>
          <button class="cart-item__remove" onclick="removeCartItem('${item.key}', this)">Entfernen</button>
        </div>
      </div>
    `).join('');
    if (subtotalEl) subtotalEl.textContent = fmt(cart.total_price);
  } catch(e) { console.error('[Wolke] Cart refresh error', e); }
}

async function removeCartItem(key, btn) {
  btn.disabled = true;
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: 0 })
  });
  refreshCartDrawer();
}
window.removeCartItem = removeCartItem;
async function changeCartItem(key, quantity) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  refreshCartDrawer();
}
window.changeCartItem = changeCartItem;

function updateCartCount(n) {
  $$('[data-cart-count]').forEach(el => {
    el.textContent = n;
    el.dataset.count = n;
  });
}

async function addToCart(variantId, quantity = 1) {
  console.log('[Wolke] addToCart →', { variantId, quantity });
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    console.error('[Wolke] /cart/add.js failed:', res.status, err);
    throw new Error(err.description || err.message || `HTTP ${res.status}`);
  }
 const data = await res.json();

window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  event: 'add_to_cart',
  ecommerce: {
    items: [{
      item_id: String(variantId),
      item_name: document.title,
      quantity: quantity
    }]
  }
});

return data;
}

/* ════════════════════════════════════
   IMAGE CAROUSEL
════════════════════════════════════ */
function initCarousel(wrapper) {
  if (!wrapper) return;
  const track = $('[data-track]', wrapper);
  const slides = $$('[data-slide]', wrapper);
  const dots = $$('[data-dot]', wrapper);
  const thumbs = $$('[data-thumb]', wrapper);
  if (!track || !slides.length) return;

  let current = 0;

  function goTo(idx) {
    current = ((idx % slides.length) + slides.length) % slides.length;
    track.style.transform = `translateX(-${current * 100}%)`;
    dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === current));
  }

  $('[data-prev]', wrapper)?.addEventListener('click', () => goTo(current - 1));
  $('[data-next]', wrapper)?.addEventListener('click', () => goTo(current + 1));
  dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));
  thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));

  // Touch swipe
  let startX = 0;
  track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 40) goTo(current + (dx < 0 ? 1 : -1));
  });

  goTo(0);
}

/* ════════════════════════════════════
   ACCORDION
════════════════════════════════════ */
function initAccordions(ctx = document) {
  // On mobile the first item ("Beschreibung") shouldn't start pre-opened
  // like it does on desktop — normalize it closed before wiring up clicks.
  if (window.matchMedia('(max-width: 767px)').matches) {
    $$('.accordion__item.is-open', ctx).forEach(item => item.classList.remove('is-open'));
  }
  $$('.accordion__trigger', ctx).forEach(btn => {
    btn.addEventListener('click', () => {
      btn.closest('.accordion__item')?.classList.toggle('is-open');
    });
  });
}

/* ════════════════════════════════════
   PRODUCT — featured section (homepage) and product page
════════════════════════════════════ */
function initProductUI(root) {
  if (!root) return;

  /* ── Shared state ── */
  let selectedVariantId = null;
  const variantsJsonEl = root.querySelector('[data-variants-json]');
  const selectedSizeEl = root.querySelector('[data-selected-size]');
  const activeColorEl  = root.querySelector('[data-active-color]');
  const colorOptIdxEl  = root.querySelector('[data-color-opt-idx]');
  const colorOptIdx    = colorOptIdxEl ? parseInt(colorOptIdxEl.value) : -1;

  /* ── Colour swatches ── */
  const swatches = $$('[data-swatch]', root);
  swatches.forEach(s => {
    s.addEventListener('click', () => {
      swatches.forEach(x => x.classList.remove('is-active'));
      s.classList.add('is-active');
      const label = $('[data-color-name]', root);
      if (label) label.textContent = s.dataset.colorName || '';
      if (activeColorEl) activeColorEl.value = s.dataset.colorName || s.title || '';
      /* If a size is already selected, re-resolve the variant */
      const activeSizeBtn = root.querySelector('.size-btn[data-size-val].is-active');
      if (activeSizeBtn) activeSizeBtn.dispatchEvent(new Event('click'));
    });
  });

  /* ── PATH A: Colour + flat Size buttons (data-size-val) ── */
  const flatSizeBtns = root.querySelectorAll('.size-btn[data-size-val]');
  if (flatSizeBtns.length && variantsJsonEl) {
    const variants = JSON.parse(variantsJsonEl.textContent);

    /* Read the currently selected colour straight from the active swatch,
       so we never depend on a (possibly wrong) initial hidden value. */
    const getActiveColor = () => {
      const sw = root.querySelector('[data-swatch].is-active');
      return sw ? (sw.dataset.colorName || sw.title || null) : null;
    };

    const resolveVariant = () => {
      const activeBtn = root.querySelector('.size-btn[data-size-val].is-active');
      if (!activeBtn) {
        selectedVariantId = null;
        if (selectedSizeEl) selectedSizeEl.textContent = '—';
        return;
      }
      const sizeVal  = activeBtn.dataset.sizeVal;
      const colValue = getActiveColor();
      /* colour sits in o1 or o2 depending on the option order; size in the other */
      const matches = variants.filter(v => {
        const sizeField  = colorOptIdx === 1 ? v.o1 : v.o2;
        const colorField = colorOptIdx === 1 ? v.o2 : v.o1;
        const sizeOK  = sizeField === sizeVal;
        const colorOK = colValue == null ? true : colorField === colValue;
        return sizeOK && colorOK;
      });
      /* Prefer an in-stock variant, but fall back to any match so the
         add-to-cart still works even if inventory tracking says 0. */
      const match = matches.find(v => v.available !== false) || matches[0];
      selectedVariantId = match ? match.id : null;
      if (selectedSizeEl) selectedSizeEl.textContent = match ? sizeVal : '—';
    };

    /* Mark sizes with no in-stock variant for the active colour as
       sold out, so the homepage matches the product page exactly. */
    const updateFlatSizeAvailability = () => {
      const colValue = getActiveColor();
      flatSizeBtns.forEach(btn => {
        const sizeVal = btn.dataset.sizeVal;
        const hasStock = variants.some(v => {
          const sizeField  = colorOptIdx === 1 ? v.o1 : v.o2;
          const colorField = colorOptIdx === 1 ? v.o2 : v.o1;
          return sizeField === sizeVal
            && (colValue == null || colorField === colValue)
            && v.available !== false;
        });
        btn.classList.toggle('is-unavailable', !hasStock);
        btn.disabled = !hasStock;
        btn.title = hasStock ? '' : 'Ausverkauft';
        if (!hasStock && btn.classList.contains('is-active')) {
          btn.classList.remove('is-active');
          selectedVariantId = null;
          if (selectedSizeEl) selectedSizeEl.textContent = '—';
        }
      });
    };

    flatSizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        flatSizeBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        resolveVariant();
      });
    });

    /* Re-evaluate availability on load and whenever the colour changes. */
    swatches.forEach(s => s.addEventListener('click', updateFlatSizeAvailability));
    updateFlatSizeAvailability();

  /* ── PATH B: Two pure size options (BH: Unterbrustumfang + Cup) ── */
  } else if (variantsJsonEl && root.querySelectorAll('[data-option-group]').length) {
    const variants  = JSON.parse(variantsJsonEl.textContent);
    const picked    = {};
    const optGroups = root.querySelectorAll('[data-option-group]');
    root.querySelectorAll('.size-btn[data-opt]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.opt;
        root.querySelectorAll(`.size-btn[data-opt="${idx}"]`).forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        picked[idx] = btn.dataset.val;
        if (Object.keys(picked).length >= optGroups.length) {
          const match = variants.find(v => v.o1 === picked['0'] && v.o2 === picked['1'] && v.available !== false);
          selectedVariantId = match ? match.id : null;
          if (selectedSizeEl) selectedSizeEl.textContent = match ? (picked['0'] + picked['1']) : '—';
        }
      });
    });

  /* ── PATH C: Single-option / placeholder ── */
  } else {
    const defaultVariant = root.querySelector('[data-default-variant]');
    if (defaultVariant) selectedVariantId = defaultVariant.value;
    const sizeBtns = $$('[data-size]', root);
    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        selectedVariantId = btn.dataset.variantId || btn.dataset.size;
      });
    });
  }

  /* Quantity */
  const qtyVal = $('[data-qty]', root);
  $('[data-dec]', root)?.addEventListener('click', () => {
    const v = parseInt(qtyVal?.textContent || '1');
    if (v > 1 && qtyVal) qtyVal.textContent = v - 1;
  });
  $('[data-inc]', root)?.addEventListener('click', () => {
    if (qtyVal) qtyVal.textContent = parseInt(qtyVal.textContent || '1') + 1;
  });

  /* ── Bundle selector: sets the quantity that gets added to cart ── */
  const bundleOpts = $$('[data-bundle-option]', root);
  if (bundleOpts.length) {
    const applyBundle = (btn) => {
      bundleOpts.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const q = parseInt(btn.dataset.bundleQty || '1');
      if (qtyVal) qtyVal.textContent = q > 0 ? q : 1;
    };
    bundleOpts.forEach(btn => btn.addEventListener('click', () => applyBundle(btn)));
    /* Initialise quantity from the pre-selected option (or the first one) */
    const initActive = root.querySelector('[data-bundle-option].is-active') || bundleOpts[0];
    if (initActive) applyBundle(initActive);
  }

  /* ATC button */
  const atcBtn = $('[data-atc]', root);
  atcBtn?.addEventListener('click', async () => {
    if (!selectedVariantId) {
      const err = $('[data-size-error]', root);
      if (err) { err.style.display = 'block'; setTimeout(() => err.style.display = 'none', 2500); }
      return;
    }
    atcBtn.classList.add('is-loading');
    const orig = atcBtn.innerHTML;
    atcBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation:spin 1s linear infinite"><path d="M21 12a9 9 0 1 1-9-9"/></svg>';
    console.log('[Wolke] ATC click — variantId:', selectedVariantId);
    try {
      const qty = parseInt(qtyVal?.textContent || '1');
      await addToCart(selectedVariantId, qty);
      atcBtn.classList.remove('is-loading');
      atcBtn.classList.add('is-added');
      atcBtn.innerHTML = '✓ HINZUGEFÜGT';
      openCartDrawer();
      setTimeout(() => { atcBtn.classList.remove('is-added'); atcBtn.innerHTML = orig; }, 2500);
    } catch(e) {
      atcBtn.classList.remove('is-loading');
      console.error('[Wolke] ATC error:', e.message);
      atcBtn.innerHTML = e.message.length < 60 ? e.message : 'Fehler — erneut versuchen';
      setTimeout(() => { atcBtn.innerHTML = orig; }, 4000);
    }
  });

  /* Buy now — adds to cart, then goes straight to checkout */
  const buyNowBtn = $('[data-buynow]', root);
  buyNowBtn?.addEventListener('click', async () => {
    if (!selectedVariantId) {
      const err = $('[data-size-error]', root);
      if (err) { err.style.display = 'block'; setTimeout(() => err.style.display = 'none', 2500); }
      return;
    }
    buyNowBtn.classList.add('is-loading');
    const orig = buyNowBtn.innerHTML;
    buyNowBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="animation:spin 1s linear infinite"><path d="M21 12a9 9 0 1 1-9-9"/></svg>';
    try {
      const qty = parseInt(qtyVal?.textContent || '1');
      await addToCart(selectedVariantId, qty);
      window.location.href = '/checkout';
    } catch(e) {
      buyNowBtn.classList.remove('is-loading');
      console.error('[Wolke] Buy now error:', e.message);
      buyNowBtn.innerHTML = e.message.length < 60 ? e.message : 'Fehler — erneut versuchen';
      setTimeout(() => { buyNowBtn.innerHTML = orig; }, 4000);
    }
  });

  /* Wishlist */
  $('[data-wish]', root)?.addEventListener('click', function() {
    this.classList.toggle('is-wished');
  });
}

/* ════════════════════════════════════
   BESTSELLER QUICK-ADD
════════════════════════════════════ */
function initQuickAdd() {
  $$('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const vid = btn.dataset.quickAdd;
      if (!vid) return;
      btn.disabled = true;
      try {
        await addToCart(vid);
        btn.classList.add('is-added');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>';
        openCartDrawer();
        setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = '+'; btn.disabled = false; }, 2000);
      } catch { btn.disabled = false; }
    });
  });
}

/* ════════════════════════════════════
   NEWSLETTER
════════════════════════════════════ */
function initNewsletter() {
  $$('[data-nl-form]').forEach(form => {
    form.addEventListener('submit', e => {
      e.preventDefault();
      const msg = $('[data-nl-msg]', form);
      if (msg) { msg.textContent = 'Danke! Du hörst bald von uns.'; msg.style.display = 'block'; }
      form.reset();
    });
  });
}

/* ── Review "Hilfreich?" voting: session-only, one vote per card ── */
function initReviewVotes() {
  $$('[data-review-helpful]').forEach(group => {
    const buttons = $$('.review-item__vote', group);
    buttons.forEach(btn => {
      btn.addEventListener('click', () => {
        const countEl = $('[data-vote-count]', btn);
        if (countEl) countEl.textContent = parseInt(countEl.textContent || '0') + 1;
        buttons.forEach(b => b.disabled = true);
      });
    });
  });
}

/* ════════════════════════════════════
   AUTO-HIDE HEADER ON SCROLL
   Slides the sticky header out of view when scrolling down, brings it
   back immediately on any upward scroll. Skipped while the cart drawer
   / mobile menu scroll-lock is active (page isn't actually scrolling).
════════════════════════════════════ */
function initAutoHideHeader() {
  const header = $('.site-header');
  if (!header) return;
  // Skip on the homepage — the transparent hero header stays sticky and
  // only swaps colors on scroll, it never hides.
  if (document.body.classList.contains('template-index')) return;
  const hideAfter = header.offsetHeight;
  let lastY = window.scrollY;
  let ticking = false;

  function onScroll() {
    ticking = false;
    if (document.body.classList.contains('scroll-locked')) return;
    const y = window.scrollY;
    if (y > lastY && y > hideAfter) {
      header.classList.add('is-hidden');
    } else {
      header.classList.remove('is-hidden');
    }
    lastY = y;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(onScroll);
      ticking = true;
    }
  }, { passive: true });
}

/* ════════════════════════════════════
   TRANSPARENT HOMEPAGE HEADER
   Header + announcement bar start fully transparent over the hero image,
   and fade to their normal solid colors once the page scrolls past where
   the header naturally docks (right below the announcement bar). Both
   stay sticky the whole time — they never hide, only their colors change,
   via a single body class so nothing is added/removed from the DOM.
════════════════════════════════════ */
function initTransparentHeader() {
  if (!document.body.classList.contains('template-index')) return;
  const header = $('.site-header');
  if (!header) return;
  const threshold = Math.max(header.offsetTop, 1);
  let ticking = false;

  function onScroll() {
    ticking = false;
    document.body.classList.toggle('nav-scrolled', window.scrollY >= threshold);
  }

  onScroll();
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(onScroll);
      ticking = true;
    }
  }, { passive: true });
}

/* ════════════════════════════════════
   SPINNER KEYFRAMES (inline)
════════════════════════════════════ */
const spinStyle = document.createElement('style');
spinStyle.textContent = '@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}';
document.head.appendChild(spinStyle);

/* ════════════════════════════════════
   INIT
════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  // Cart count on load
  fetch('/cart.js').then(r => r.json()).then(c => updateCartCount(c.item_count)).catch(() => {});

  // Carousels
  $$('[data-carousel]').forEach(initCarousel);

  // Accordions
  initAccordions();

  // Product UI (works on both homepage featured-product and product page)
  $$('[data-product-ui]').forEach(initProductUI);

  // Quick-add on bestsellers grid
  initQuickAdd();

  // Newsletter
  initNewsletter();

  // Review "Hilfreich?" voting
  initReviewVotes();

  // Auto-hide header on scroll
  initAutoHideHeader();

  // Transparent header over the homepage hero
  initTransparentHeader();
});
